import React from 'react';
import PropTypes from 'prop-types';
import range from 'lodash/range';
import { Roles } from '../../../../../core/util';
import Select from '../../../../../core/form/Select';
import Button from '../../../../../core/form/Button';
import Table from '../../../../../core/form/Table';
import ItemFormContainer from '../../../../item/components/ItemFormContainer';
import { browserHistory } from 'react-router';
import Tag from '../../../../../core/layout/Tag';
import { startCase } from 'lodash';
import Text from '../../../../../core/layout/Text';
import AudioPreview from '../../../../../core/layout/AudioPreview';
import ImagePreview from '../../../../../core/layout/ImagePreview';
import VideoPreview, { Modes } from '../../../../../core/layout/VideoPreview';

const UnitItemList = (props) => (
  <Table
    fetching={props.fetching}
    rowClassName={(row) =>
      row.item.status === 'DRAFT' || row.item.status === 'UNPUBLISHED' ? 'table-row-dark' : 'table-row-light'
    }
    columns={[
      {
        label: 'Order',
        path: 'order',
        width: '75px',
        sort: true,
        render: (cell, row) => {
          if (
            [Roles.ADMIN, Roles.SUPPORT, Roles.CONTENT_ADMIN].some((r) => r === localStorage.role) &&
            (row.item.status !== 'DRAFT' || !row.item.parent)
          ) {
            return (
              <Select
                label="Order"
                disabled={props.submitting || props.disabled || props.selectable}
                value={row.order}
                onChange={(order) => props.onOrderOrGroupChange(row, order, row.group)}
                options={range(1, 61).map((value) => ({
                  label: value.toString(),
                  value,
                }))}
              />
            );
          }

          return cell;
        },
      },
      {
        label: 'Group',
        path: 'group',
        width: '130px',
        render: (cell, row) => {
          if (
            [Roles.ADMIN, Roles.SUPPORT, Roles.CONTENT_ADMIN].some((r) => r === localStorage.role) &&
            (row.item.status !== 'DRAFT' || (row.item.status === 'DRAFT' && !row.item.parent))
          ) {
            return (
              <Select
                label="Group"
                disabled={props.submitting || props.disabled || props.selectable}
                value={row.group}
                onChange={(group) => props.onOrderOrGroupChange(row, row.order, group)}
                options={[
                  {
                    label: 'Default',
                    value: 1,
                  },
                  {
                    label: 'First Review',
                    value: 2,
                  },
                  {
                    label: 'Second Review',
                    value: 3,
                  },
                ]}
              />
            );
          }
          return ['', 'Default', 'First Review', 'Second Review'][row.group];
        },
      },
      {
        label: 'Text',
        path: 'item.text',
        width: '20%',
        render: (cell, row) => <Text value={row.item.text ? row.item.text : row.item.title} maxLength={150} />,
      },
      {
        label: 'Portuguese Translation',
        path: 'item.translation',
        width: '20%',
        render: (cell) => <Text value={cell} maxLength={150} />,
      },
      {
        label: 'Spanish Translation',
        path: 'item.spanishTranslation',
        width: '20%',
        render: (cell) => <Text value={cell} maxLength={150} />,
      },
      {
        label: 'Grammar',
        path: 'item.grammar.name',
        width: '150px',
      },
      {
        label: 'Type',
        path: 'item.type.name',
        width: '100px',
        sort: true,
        render: (cell, row) => (
          <span style={{ color: row.item.invalidItemType ? 'red' : 'inherited' }}>
            <Tag color={{ DRAFT: 'grey', PUBLISHED: 'green', UNPUBLISHED: 'red' }[row.item.status]}>
              {startCase(row.item.status.toLowerCase())}
            </Tag>
            {cell}
          </span>
        ),
      },
      {
        label: 'Audio',
        path: 'item.audio',
        width: '70px',
        render: (cell, row) => (
          <>
            {!!cell && (
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <AudioPreview src={cell} />
              </div>
            )}
            {row.item.answers && row.item.answers.length > 0 && row.item.answers.some((a) => a.audio) && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: row.item.answers.filter((a) => a.audio).length > 1 ? '25px 25px' : '25px',
                  marginTop: 10,
                  gridGap: 5,
                  justifyContent: 'center',
                }}
              >
                {row.item.answers.map((a) => (a.audio ? <AudioPreview src={a.audio} size="sm" /> : null))}
              </div>
            )}
          </>
        ),
      },
      {
        label: 'Post Phrase Audio',
        path: 'item.postPhraseAudio',
        width: '70px',
        render: (cell, row) => (
          <>
            {!!cell && (
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <AudioPreview src={cell} />
              </div>
            )}
            {row.item.answers && row.item.answers.length > 0 && row.item.answers.some((a) => a.postPhraseAudio) && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns:
                    row.item.answers.filter((a) => a.postPhraseAudio).length > 1 ? '25px 25px' : '25px',
                  marginTop: 10,
                  gridGap: 5,
                  justifyContent: 'center',
                }}
              >
                {row.item.answers.map((a) =>
                  a.postPhraseAudio ? <AudioPreview src={a.postPhraseAudio} size="sm" /> : null
                )}
              </div>
            )}
          </>
        ),
      },
      {
        label: 'Image',
        path: 'item.image',
        width: '190px',
        render: (cell, row) => (
          <>
            {!!cell && <ImagePreview src={cell} renderType="image" />}
            {row.item.answers && row.item.answers.length > 0 && row.item.answers.some((a) => a.image) && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: row.item.answers.filter((a) => a.image).length > 1 ? '80px 80px' : '80px',
                  marginTop: cell ? 10 : 0,
                  gridGap: 5,
                  justifyContent: 'center',
                }}
              >
                {row.item.answers.map((a) =>
                  a.image ? <ImagePreview src={a.image} renderType="image" size="sm" /> : null
                )}
              </div>
            )}
          </>
        ),
      },
      {
        label: 'Vídeo',
        path: 'item.videoExternalLink',
        width: '70px',
        render: (cell, row) => {
          if (cell) {
            return (
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <VideoPreview src={cell} id={`${cell}-list-item`} mode={Modes.BUTTON_MODAL} />
              </div>
            );
          } else if (row.item.videoLink) {
            return (
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <VideoPreview
                  src={row.item.videoLink}
                  id={`${row.item.videoLink}-list-item`}
                  mode={Modes.BUTTON_MODAL}
                />
              </div>
            );
          }
        },
      },
      {
        label: 'Actions',
        path: 'action',
        width: props.disabled ? '0px' : '150px',
        render: (cell, row, index) => {
          if (
            [Roles.ADMIN, Roles.SUPPORT, Roles.CONTENT_ADMIN, Roles.DISTRIBUTOR_CONTENT_ADMIN].some(
              (r) => r === localStorage.role
            ) &&
            !props.disabled
          ) {
            return (
              <div>
                {row.item.status === 'DRAFT' && (
                  <>
                    <Button
                      icon="delete"
                      tooltip="Remove"
                      disabled={props.selectable}
                      onClick={() => props.onDelete(row)}
                    />{' '}
                    <Button
                      icon="cloud-upload"
                      tooltip="Publish"
                      disabled={props.selectable}
                      onClick={() => props.onPublish(row)}
                    />
                  </>
                )}
                {row.item.status === 'PUBLISHED' && (
                  <>
                    <Button
                      icon="edit"
                      tooltip="Create draft"
                      disabled={props.selectable}
                      onClick={() => props.onDraft(row)}
                    />{' '}
                    <Button
                      icon="copy"
                      tooltip="Duplicate"
                      disabled={props.selectable}
                      onClick={() =>
                        browserHistory.push({
                          pathname: `/modules/${props.unit.module.id}/units/${props.unit.id}/items/new`,
                          state: { item: row.item },
                        })
                      }
                    />{' '}
                    <Button
                      icon="cloud-download"
                      tooltip="Unpublish"
                      disabled={props.selectable}
                      onClick={() => props.onUnpublish(row)}
                    />{' '}
                    {!!row.item.parent && (
                      <>
                        <Button
                          icon="history"
                          tooltip="Show history"
                          disabled={props.selectable}
                          onClick={() =>
                            browserHistory.push(
                              `/modules/${props.unit.module.id}/units/${props.unit.id}/items/${row.item.id}/history`
                            )
                          }
                        />{' '}
                      </>
                    )}
                    <Button
                      label="+1"
                      disabled={props.selectable}
                      onClick={() => props.onAutoReorder(index, 'ADD_LINE')}
                    />{' '}
                    <Button
                      label="-1"
                      disabled={props.selectable}
                      onClick={() => props.onAutoReorder(index, 'REMOVE_LINE')}
                    />
                  </>
                )}
                {row.item.status === 'UNPUBLISHED' && (
                  <>
                    <Button
                      icon="cloud-upload"
                      tooltip="Publish"
                      disabled={props.selectable}
                      onClick={() => props.onPublish(row)}
                    />
                  </>
                )}
              </div>
            );
          }
          return null;
        },
      },
    ]}
    rows={props.items}
    selectable={false}
    getCheckboxProps={props.getCheckboxProps}
    {...(props.selectable && { selectedRows: props.selectedRows, onSelectRows: props.onSelectRows })}
    {...(!props.selectable && {
      expandableComponent: (row) => (
        <div style={{ padding: '0px 10px' }}>
          <ItemFormContainer
            itemId={row.item.id}
            itemsTypeUrl={`unit-types/${props.unit.type.id}/item-types`}
            endpointUrl={`units/${props.unit.id}/items`}
            order={row.order}
            disabled={row.item.status === 'PUBLISHED' || props.disabled}
            showPostPhrase={props.unit.type.name.toLowerCase() === 'vocabulary'}
          />
        </div>
      ),
    })}
  />
);

UnitItemList.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      item: PropTypes.shape({
        text: PropTypes.string,
        translation: PropTypes.string,
        grammar: PropTypes.shape({
          name: PropTypes.string.isRequired,
        }),
      }),
    })
  ).isRequired,
  unit: PropTypes.shape({
    id: PropTypes.string.isRequired,
    createdBy: PropTypes.string.isRequired,
    module: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
    type: PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  fetching: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  submitting: PropTypes.bool,
  onOrderOrGroupChange: PropTypes.func.isRequired,
  onAutoReorder: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onDraft: PropTypes.func.isRequired,
  onPublish: PropTypes.func.isRequired,
  onUnpublish: PropTypes.func.isRequired,
  selectedRows: PropTypes.arrayOf(PropTypes.string),
  onSelectRows: PropTypes.func,
  selectable: PropTypes.bool,
  getCheckboxProps: PropTypes.func,
};

UnitItemList.defaultProps = {
  submitting: false,
  selectable: false,
  getCheckboxProps: () => false,
};

export default UnitItemList;
